import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { plato_api_revert_image_models_versions_path } from '@/modules/routes';
import { useMutation } from '@tanstack/react-query';
import { queryClient } from '@/utils/ReactQuery';
import useVersions from '@/hooks/api/v2/useVersions';
import useApiImages from '@/hooks/api/v2/useImages';
import useApiImageModels from '@/hooks/api/v2/useImageModels';

type HookArgs = { isEnabled?: boolean, image: any };

export function useMergedVersions({ image, isEnabled = true }: HookArgs) {
  return useVersions({
    enabled: isEnabled,
    only: ['created_at', 'id', 'item_id', 'meta'],
    params: {
      merged_to: image.id,
      'search[event_eq]': 'merge',
      'search[item_type_eq]': 'ImageModel',
    },
  });
}

export function useImages({ image, isEnabled = true }) {
  const { data: versions } = useMergedVersions({ image, isEnabled });
  const imageIds = versions?.map(v => v.meta.merged_from) || [];

  return useApiImages({
    enabled: imageIds.length > 0,
    only: ['id', 'image_en_file_name', 'created_at', 'url'],
    params: { 'search[id_in]': imageIds },
  });
}

export type ImageResponse = Prettify<ReturnType<typeof useImages>['data'][0]>;

export function useImageModels({ image, isEnabled = true }: HookArgs) {
  return useApiImageModels({
    enabled: isEnabled,
    only: ['id', 'image_id'],
    params: { 'search[image_id_eq]': image.id },
  });
}

export function useMergedImageModels({ image, isEnabled = true }) {
  const { data: versions } = useMergedVersions({ image, isEnabled });
  const imageModelIds = versions.map(v => v.item_id);

  return useApiImageModels({
    enabled: isEnabled && imageModelIds.length > 0,
    only: ['id', 'image_id'],
    params: { 'search[id_in]': imageModelIds },
  });
}

export type ImageModelResponse = Prettify<ReturnType<typeof useImageModels>['data'][0]>;

export function useVersionImageModels({ image, isEnabled = true }) {
  const { data: mergedVersions } = useMergedVersions({ image, isEnabled });
  const imageIds = mergedVersions.map(version => version.meta?.merged_from);

  return useApiImageModels({
    enabled: imageIds.length > 0 && isEnabled,
    only: ['id', 'image_id'],
    params: { 'search[image_id_in]': imageIds },
    placeholderData: [],
  });
}

export function useEnableOnHover() {
  const ref = useRef<HTMLButtonElement>();
  const [hasHovered, setHasHovered] = useState(false);

  useLayoutEffect(() => {
    if (!ref?.current) return () => {};

    const row = ref.current.closest('tr');

    if (!row) return () => {};

    const fetchFn = () => {
      if (hasHovered) return;

      setHasHovered(true);
    };

    row.addEventListener('mouseenter', fetchFn);

    return () => row.removeEventListener('mouseenter', fetchFn);
  }, [ref?.current, hasHovered]);

  return { isEnabled: hasHovered, ref, setHasHovered };
}

export function useMergedVersionCount({ image, isEnabled = true }) {
  const { data: versions } = useMergedVersions({ image, isEnabled });
  const { data: imageModels } = useImageModels({ image, isEnabled });

  return useMemo(() => (
    versions.reduce((acc, cur) => {
      const imageModel = imageModels?.find(im => im.id === cur.item_id);

      return acc + (imageModel ? 1 : 0);
    }, 0)
  ), [versions, imageModels]);
}

export function useRevertedVersions({ image, imageModels }) {
  return useVersions({
    enabled: imageModels.length > 0,
    only: ['id', 'created_at'],
    params: { revert_to: image.id, 'search[event_eq]': 'revert', 'search[item_type_eq]': 'ImageModel' },
  });
}

export function usePreviousVersions({ image, targetImage, isEnabled }) {
  return useVersions({
    enabled: isEnabled,
    only: ['id', 'created_at', 'meta'],
    params: {
      merged_from: image.id,
      merged_to: targetImage.id,
      'search[event_eq]': 'merge',
      'search[item_type_eq]': 'ImageModel',
    },
  });
}

export function useRevertMutation({ image, setRevertedVersions }) {
  const { queryKey: useImageModelsQueryKey } = useImageModels({ image, isEnabled: false });
  const { data: mergedVersions, queryKey: useVersionsQueryKey } = useMergedVersions({ image, isEnabled: false });
  const { queryKey: useVersionImageModelsQueryKey } = useVersionImageModels({ image, isEnabled: false });

  return useMutation({
    mutationFn: ({ ids }: { ids: string[] }) => {
      const versionIds = mergedVersions
        .filter(version => ids.includes(version.meta?.merged_from?.toString()))
        .map(version => version.id);

      return (
        axios
          .post(plato_api_revert_image_models_versions_path({ 'search[id_in]': versionIds }))
          .then(async (res) => {
            await queryClient.invalidateQueries(useImageModelsQueryKey);
            await queryClient.invalidateQueries(useVersionsQueryKey);
            await queryClient.invalidateQueries(useVersionImageModelsQueryKey);

            setRevertedVersions(res.data.data);
          })
      );
    },
  });
}
